import React from 'react';
import { reaction } from 'mobx';
import { DrawerService, EventEmitter, InjectorService } from '@tencent/workbench';
import { DRAWER_PROPERTY_EDITOR_WIDTH } from '@src/framework/editor/src/js/constants';
import { SelectWindowShowAction } from '@src/js/constant';
import { DrawerName, WbEventNames } from '@src/framework/workbench/drawer/constants';
import SelectWindow from './component';
import { SELECT_WINDOW_REPORT, tdwReport } from './component/report';
import { ISelectWindowManager, ISelectWindowManagerServiceDelegates } from './select-window-manager.interface';
import { IDrawerStateManager } from '../../drawer-state-manager/core/drawer-state-manager.interface';
import DrawerTopTab from '@framework/workbench/drawer/components/drawer-top-tab';
import type { Subscription } from 'rxjs';

const SELECT_WINDOW_MAX_WIDTH = 500;

export class SelectWindowManager implements ISelectWindowManager {
    private drawerService: DrawerService;
    private selectWindowWidth = DRAWER_PROPERTY_EDITOR_WIDTH;
    private services: ISelectWindowManagerServiceDelegates;
    private drawerStateManager?: IDrawerStateManager;
    private eventEmitter: EventEmitter;
    private rightDrawerOpenListener: Subscription | undefined;

    public setServiceDelegates(services: ISelectWindowManagerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
    }

    /**
     * 初始化选择窗格侧边栏
     */
    public init = (drawerStateManager?: IDrawerStateManager) => {
        this.drawerStateManager = drawerStateManager;
        this.drawerService = InjectorService.get(DrawerService);
        this.eventEmitter = InjectorService.get(EventEmitter);
        reaction(
            () => this.services.rightSideBarManager?.isSelectWindowShown,
            (show) => {
                if (show) {
                    this.drawerStateManager?.openTab(DrawerName.SELECT_WINDOW_RIGHT_DRAWER);
                    this.showSelectWindow();
                } else {
                    this.drawerStateManager?.closeRightDrawer(DrawerName.SELECT_WINDOW_RIGHT_DRAWER);
                    const { rightSideBarManager } = this.services;
                    if (rightSideBarManager?.selectWindowShowAction === SelectWindowShowAction.FROM_FORMAT) {
                        rightSideBarManager?.setSelectWindowShowAction(SelectWindowShowAction.NONE);
                        rightSideBarManager?.setIsPropertyEditorShown(true);
                        return;
                    }
                }
                window.dispatchEvent(new Event('resize'));
            },
        );
    };

    private showSelectWindow = () => {
        this.drawerService.showRightDrawer({
            name: DrawerName.SELECT_WINDOW_RIGHT_DRAWER,
            content: <DrawerTopTab>
                <SelectWindow zIndex={1} services={this.services} drawerStateManager={this.drawerStateManager}/>
            </DrawerTopTab>,
            style: {
                borderBottom: '1px solid #e4e4e4',
                padding: '0px',
                backgroundColor: '#fcfcfc',
                width: this.selectWindowWidth,
            },
            onClose: this.closeSelectWindow,
            edgeDraggable: {
                maxWidth: SELECT_WINDOW_MAX_WIDTH,
                onDragEnd: this.handleSelectWindowDragEnd,
            },
        });
        this.drawerStateManager?.handleDrawerHolderVisiable(true);
    };

    private closeSelectWindow = () => {
        this.drawerStateManager?.handleDrawerHolderVisiable(false);
        if (this.isOtherDrawerOpen()) {
            this.rightDrawerOpenListener = this.eventEmitter.listen(WbEventNames.RIGHT_DRAWER_CLOSED, () => {
                this.showSelectWindow();
                this.rightDrawerOpenListener?.unsubscribe();
            });
            return;
        }
        this.drawerStateManager?.closeRightDrawer(DrawerName.SELECT_WINDOW_RIGHT_DRAWER);
        this.services.rightSideBarManager?.setIsSelectWindowShown(false);
    };

    private isOtherDrawerOpen = (): boolean | undefined => this.services.rightSideBarManager?.isSelectWindowShown
        && !(this.drawerService.isRightOpening(DrawerName.SLIDE_PROPERTY_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.ANIMATION_RIGHT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.COMMENT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.SELECT_WINDOW_RIGHT_DRAWER)
        )
        && this.drawerService.isRightOpening();

    private handleSelectWindowDragEnd = async (width: number) => {
        this.selectWindowWidth = width;
        this.services.rightSideBarManager?.setSelectWindowWidth(width);
        this.drawerStateManager?.updateDrawerHolderStyle();
        window.dispatchEvent(new Event('resize'));
        tdwReport({ ...SELECT_WINDOW_REPORT.setWidth, ver5: width });
    };
}
